"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Briefcase, Search, ArrowRight, ShieldCheck, Loader2, PhoneCall, MapPin, X, Zap, MessageCircle } from "lucide-react";

import {
  forgetFixitJob,
  listRememberedFixitJobs,
  type RememberedFixitJob,
} from "@/lib/fixIt/myJobsStorage";

export default function FindMyJobsPage() {
  const router = useRouter();
  const [jobCode, setJobCode] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Device-saved jobs. Read in an effect so the server HTML (empty list) matches
  // the first client render — the list fills in immediately after hydration.
  const [myJobs, setMyJobs] = useState<RememberedFixitJob[]>([]);
  useEffect(() => {
    setMyJobs(listRememberedFixitJobs());
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch("/api/fix-it/my-jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobCode, surname, phone }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.ok) {
        setError(data.error || "We couldn't find that job. Please check and try again.");
        return;
      }
      router.push(data.redirectTo);
    } catch {
      setError("Something went wrong. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 placeholder:text-slate-400";

  return (
    <main className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-slate-50 px-4 py-10 sm:py-16">
      <div className="mx-auto max-w-lg">
        <Link href="/fix-it" className="text-sm font-medium text-emerald-700 hover:text-emerald-800">
          &larr; Back to Fix-it
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <Briefcase className="h-5 w-5" aria-hidden />
          </span>
          <h1 className="text-2xl font-bold text-slate-900">My Jobs</h1>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Every job you opened on this phone or computer, ready to reopen — no login
          needed. Come back any time to follow the work or leave your review once
          things are back to normal.
        </p>

        {/* ── Jobs saved on this device ─────────────────────────────────── */}
        {myJobs.length > 0 ? (
          <div className="mt-6 space-y-3">
            {myJobs.map((job) => (
              <div
                key={job.token}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-900">
                    {job.serviceType || "Fix-it job"}
                    {job.jobCode ? (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold tracking-wide text-slate-600">
                        {job.jobCode}
                      </span>
                    ) : null}
                  </p>
                  {job.cityTown || job.province ? (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {[job.cityTown, job.province].filter(Boolean).join(", ")}
                    </p>
                  ) : null}
                  {job.squadName ? (
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      Your squad: {job.squadName}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-col gap-1.5">
                  <Link
                    href={`/fix-it/track?token=${encodeURIComponent(job.token)}`}
                    className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
                  >
                    Open job
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                  {job.squadOwnerId ? (
                    <Link
                      href={`/fix-it/request?proId=${encodeURIComponent(job.squadOwnerId)}&service=${encodeURIComponent(job.serviceType || "")}&urgency=today`}
                      className="flex items-center gap-1.5 rounded-xl border border-[#001489] px-4 py-2 text-xs font-semibold text-[#001489] transition hover:bg-[#eef2ff]"
                    >
                      <Zap className="h-3.5 w-3.5" aria-hidden />
                      Book {job.squadName || "this squad"} again
                    </Link>
                  ) : null}
                </div>
                <button
                  type="button"
                  aria-label="Remove this job from this device"
                  onClick={() => {
                    forgetFixitJob(job.token);
                    setMyJobs(listRememberedFixitJobs());
                  }}
                  className="shrink-0 rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            ))}
            <p className="px-1 text-xs text-slate-400">
              Jobs are saved only on this device. To remove one, tap the ×.
            </p>
          </div>
        ) : (
          <p className="mt-6 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500 shadow-sm">
            No jobs saved on this device yet. When you open a job&apos;s tracking link,
            it is remembered here automatically.
          </p>
        )}

        {/* ── Lookup by reference — for a lost link or a new device ─────── */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <Search className="h-5 w-5" aria-hidden />
            </span>
            <h2 className="text-xl font-bold text-slate-900">Find a job by reference</h2>
          </div>

          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Lost your tracking link, or on a new phone? Enter the job reference we
            sent you and we&apos;ll take you straight back to your job.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="jobCode" className="block text-sm font-semibold text-slate-700">
                Job reference
              </label>
              <input
                id="jobCode"
                name="jobCode"
                value={jobCode}
                onChange={(e) => setJobCode(e.target.value.toUpperCase())}
                placeholder="KQP-482"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                inputMode="text"
                maxLength={12}
                required
                className={`${inputClass} mt-1.5 font-mono tracking-widest`}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                It looks like three letters, a dash and three numbers.
              </p>
            </div>

            <div>
              <label htmlFor="surname" className="block text-sm font-semibold text-slate-700">
                Surname
              </label>
              <input
                id="surname"
                name="surname"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                placeholder="Mokoena"
                autoComplete="family-name"
                className={`${inputClass} mt-1.5`}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-700">
                Or the mobile number you used
              </label>
              <input
                id="phone"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="082 123 4567"
                autoComplete="tel"
                inputMode="tel"
                className={`${inputClass} mt-1.5`}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                Use your surname or your number &mdash; either one is enough.
              </p>
            </div>

            {error ? (
              <div
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={busy || !jobCode.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                  Finding your job&hellip;
                </>
              ) : (
                <>
                  Find my job
                  <ArrowRight className="h-5 w-5" aria-hidden />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 flex items-start gap-2.5 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
            <p>
              We ask for two details so nobody else can open your job. We never ask for your
              ID number, card details or a password.
            </p>
          </div>
        </div>

        {/* ── Still stuck? — Zamzi's OWN published number, with a choice
            of channel. A customer lost at this screen may hate calling;
            WhatsApp with a prefilled message gives them the alternative and
            the same number works for both (the floating site-wide button
            uses 27774584896 too). */}
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <PhoneCall className="h-4 w-4 text-emerald-600" aria-hidden />
            Still stuck?
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
            Call or WhatsApp us with the mobile number you booked on and we&apos;ll find your job for you.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <a
              href="tel:+27774584896"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              <PhoneCall className="h-4 w-4" aria-hidden />
              Call Zamzi
            </a>
            <a
              href="https://wa.me/27774584896?text=Hi%20Zamzi%2C%20I%20need%20help%20finding%20my%20Fix-it%20job."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              WhatsApp Zamzi
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}
